import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAppFlags } from '@/hooks/useAppFlags';
import { useMediumScreen, useSmallScreen } from '@/hooks/useWindowDimension';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useDeliveryIdentifier } from '@/hooks/useDeliveryIdentifier';
import TagManager from 'react-gtm-module';
import { updateViewState } from '@/features/analysisMapSlice';
import { GoogleEvents } from '@/features/googleEvents';
import { useListReleasesQuery } from '@/features/adminSlice';
import { useActiveClientCode } from '@/hooks/useActiveClientCode';
import { useReleaseId } from '@/hooks/useReleaseId';
import { useResetClientAndApiState } from '@/views/useResetClientAndApiState';
import { useClientTerminology } from '@/hooks/useClientTerminology';
import {
  ReleaseStatusEffective,
  type ReleaseRow,
} from '@/generated/types/navigator/types.gen';
import { ReleaseSelectorDesktop } from './ReleaseSelectorDesktop';
import { ReleaseSelectorMobile } from './ReleaseSelectorMobile';
import { ReleaseSelectorProgram } from './ReleaseSelectorProgram';

export type ReleaseOption = { value: string; label: string };

type Props = {
  iconSize?: number;
  customClassName?: string;
  mode?: 'shadowed' | 'light' | 'program';
  showAnalysisDateLabel?: boolean;
};

// A release stands for the scan it was made from, so its position and default label
// follow the scan date rather than when the release itself was created.
const releaseDate = (r: ReleaseRow): Date =>
  new Date(r.scan_started_at ?? r.created_at);

const buildOptions = (releases: ReleaseRow[]): ReleaseOption[] => {
  const sorted = [...releases].sort(
    (a, b) => releaseDate(b).getTime() - releaseDate(a).getTime()
  );

  const yearCounts = new Map<string, number>();
  for (const r of sorted) {
    const y = releaseDate(r).getFullYear().toString();
    yearCounts.set(y, (yearCounts.get(y) ?? 0) + 1);
  }

  return sorted.map((r) => {
    const date = releaseDate(r);
    const year = date.getFullYear().toString();
    const collides = (yearCounts.get(year) ?? 0) > 1;
    const month = date.toLocaleString(undefined, { month: 'short' });
    const dateLabel = collides ? `${year} — ${month}` : year;
    return {
      value: r.id,
      label: r.display_name?.trim() || dateLabel,
    };
  });
};

export const ReleaseSelector = ({
  customClassName,
  iconSize = 16,
  mode = 'shadowed',
  showAnalysisDateLabel = false,
}: Props) => {
  const intl = useClientTerminology();
  const { flagEnableAnalysisHistoryForAll } = useAppFlags();
  const clientCode = useActiveClientCode();
  const isPreviewingDelivery = useDeliveryIdentifier() !== undefined;
  const { data: releases } = useListReleasesQuery(
    clientCode
      ? {
          clientCode,
          statuses: flagEnableAnalysisHistoryForAll
            ? [ReleaseStatusEffective.PUBLISHED]
            : [
                ReleaseStatusEffective.PUBLISHED,
                ReleaseStatusEffective.COMBINED,
              ],
        }
      : skipToken
  );
  const releaseId = useReleaseId();
  const [, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const resetClientAndApiState = useResetClientAndApiState();

  const { mapState, clientIsLoaded } = useAppSelector((s) => s.analysisMap);
  const isSmallScreen = useSmallScreen();
  const isMediumScreen = useMediumScreen();
  const isCompactVariant = isSmallScreen || isMediumScreen;

  const { releaseOptions, newestPublishedId } = useMemo(() => {
    const all = releases ?? [];
    const published = buildOptions(
      all.filter((r) => r.effective_status === ReleaseStatusEffective.PUBLISHED)
    );
    const newestPublishedId = published[0]?.value;
    const combined = flagEnableAnalysisHistoryForAll
      ? undefined
      : all.find((r) => r.effective_status === ReleaseStatusEffective.COMBINED);
    if (!combined) {
      // Show latest published release (with no combined release).
      return { releaseOptions: published, newestPublishedId };
    }
    const latest: ReleaseOption = {
      value: combined.id,
      label: intl.formatMessage({ id: 'releaseSelector.latest' }),
    };
    // Show the combined release first, then the published releases.
    return {
      releaseOptions: [latest, ...published],
      newestPublishedId: combined.id,
    };
  }, [releases, intl, flagEnableAnalysisHistoryForAll]);
  const selectedReleaseId = releaseId ?? newestPublishedId ?? '';

  const writeRelease = (id: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('release', id);
      next.delete('delivery');
      return next;
    });
  };

  const handleChange = (id: string) => {
    if (id === selectedReleaseId) return;

    if (clientIsLoaded) dispatch(updateViewState(mapState));

    writeRelease(id);
    resetClientAndApiState();

    const labelOf = (lookup: string) =>
      releaseOptions.find((o) => o.value === lookup)?.label ?? '';

    TagManager.dataLayer({
      dataLayer: {
        event: GoogleEvents.releaseSelectorChanged,
        from: labelOf(selectedReleaseId).replace(/\s+/g, '_'),
        to: labelOf(id).replace(/\s+/g, '_'),
      },
    });
  };

  const selectedInOptions = releaseOptions.some(
    (o) => o.value === selectedReleaseId
  );
  if (
    !clientCode ||
    !selectedReleaseId ||
    !selectedInOptions ||
    releaseOptions.length <= 1 ||
    isPreviewingDelivery
  ) {
    return null;
  }

  if (mode === 'program') {
    return (
      <ReleaseSelectorProgram
        options={releaseOptions}
        selectedId={selectedReleaseId}
        onChange={handleChange}
        customClassName={customClassName}
      />
    );
  }

  if (isCompactVariant) {
    return (
      <ReleaseSelectorMobile
        options={releaseOptions}
        selectedId={selectedReleaseId}
        onChange={handleChange}
      />
    );
  }

  return (
    <ReleaseSelectorDesktop
      options={releaseOptions}
      selectedId={selectedReleaseId}
      onChange={handleChange}
      iconSize={iconSize}
      mode={mode}
      customClassName={customClassName}
      showAnalysisDateLabel={showAnalysisDateLabel}
    />
  );
};
